import { useColorMode } from "@vueuse/core";
import { ref, computed, watchEffect } from "vue";
import wabtecLogo from "@/assets/sidebar-logo-wabtec.svg";
import tgLogo from "@/assets/tg_logo_white_no_bg.png";
import aitxLogo from "@/assets/logo_aitx_sidebar.svg";
import dayjs from "dayjs";

type ColorMode = "light" | "dark" | "auto";
type AcceptableTheme = "dev" | "prod" | "wabtec" | "aitx";
const activeTheme = ref<AcceptableTheme>();
const colorMode = useColorMode();

export function useTheme() {
  const defaultTheme = computed(() => {
    if (window.location.origin.includes("railconnect")) {
      return "wabtec";
    }

    if (window.location.origin.includes("aitx")) {
      return "aitx";
    }

    if (import.meta.env.PROD) {
      return "prod";
    }

    return "dev";
  });

  const logoUrl = computed(() => {
    switch (activeTheme.value) {
      case "wabtec":
        return wabtecLogo;
      case "aitx":
        return aitxLogo;
      default:
        return tgLogo;
    }
  });

  const favicon = computed(() => {
    switch (activeTheme.value) {
      case "wabtec":
        return "/wabtec_favicon.ico";
      case "aitx":
        return "/favicon_aitx.ico";
      default:
        return "/favicon.ico";
    }
  });

  const pageTitle = computed(() => {
    switch (activeTheme.value) {
      case "wabtec":
        return "RailConnect - Enhanced Visibility";
      case "aitx":
        return "AITX Dashboard";
      default:
        return "Telegraph";
    }
  });

  function setTheme(theme: "dev" | "prod" | "wabtec" | "aitx") {
    activeTheme.value = theme;

    if (theme !== defaultTheme.value) {
      localStorage.setItem("platform_theme", theme);

      if (localStorage.getItem("platform_theme_set_at") === null) {
        localStorage.setItem("platform_theme_set_at", dayjs().toISOString());
      }
    }

    const html = document.querySelector("html");
    if (html) {
      html.setAttribute("data-theme", theme);
    }
  }

  function setTitle(title: string) {
    document.title = `${pageTitle.value} ${title}`;
  }

  function setColorMode(mode: ColorMode) {
    colorMode.value = mode;
  }

  function applyThemeOnInit() {
    const storedTheme = localStorage.getItem("platform_theme");
    const storedThemeSetAt = localStorage.getItem("platform_theme_set_at");

    if (!storedTheme) {
      localStorage.removeItem("platform_theme_set_at");
      return setTheme(defaultTheme.value);
    }

    const parsed = dayjs(storedThemeSetAt);
    const expired = parsed.isValid() ? parsed.isBefore(dayjs().subtract(24, "hours")) : true;
    if (expired) {
      localStorage.removeItem("platform_theme_set_at");
      return setTheme(defaultTheme.value);
    }

    return setTheme(storedTheme as AcceptableTheme);
  }

  applyThemeOnInit();

  watchEffect(() => {
    const link = document.querySelector("link[rel='icon']");
    if (link) {
      link.setAttribute("href", favicon.value);
    }
  });

  return { activeTheme, setTheme, logoUrl, setTitle, colorMode, setColorMode };
}
